import {
  type Balance,
  cents,
  formatMoney,
  jobPlanStanding,
  longDate,
  planSentCents,
} from "@roof-hawk/core";

import { t } from "../i18n";
import type { JobPlan } from "../lib/queries";

/**
 * Where a customer on an in-house payment plan stands (ADR 0286).
 *
 * Every recorded plan payment counts here, banked or still clearing: whether
 * a customer is late is about whether they sent the check, not whether
 * Mercury has seen it yet. The balance above is still the bank's answer.
 */
export function PlanStanding({
  plan,
  balance,
  now,
}: {
  plan: JobPlan | null | undefined;
  balance: Balance;
  now: Date;
}) {
  if (!plan) return null;
  const standing = jobPlanStanding(
    balance,
    plan.terms,
    planSentCents(plan.payments),
    localDay(now),
  );
  if (!standing) return null;
  const monthly = formatMoney(cents(plan.terms.monthlyCents));
  const { paymentsMade: made, paymentsTotal: total, daysLate: days } = standing;
  const date = longDate(standing.overdueSince ?? "");

  return (
    <div className={standing.late ? "bal-live" : "bal-way"} role="status">
      <div>
        {t(
          "Payment plan: {monthly} a month. {made} of {total} payments made, {left} left on the plan.",
          { monthly, made, total, left: formatMoney(standing.remaining) },
        )}
      </div>
      {standing.paidOff ? (
        <div>{t("The plan is paid off.")}</div>
      ) : standing.late ? (
        <div>
          {t(
            "Late: {amount} was due {date}, {days} days ago. Call the customer.",
            { amount: formatMoney(standing.behind), date, days },
          )}
        </div>
      ) : standing.next ? (
        <div>
          {t("Next: {amount} due {date}.", {
            amount: formatMoney(standing.next.amount),
            date: longDate(standing.next.dueOn),
          })}
        </div>
      ) : null}
    </div>
  );
}

/** The calendar day `now` falls on, here, as `YYYY-MM-DD`. */
function localDay(now: Date): string {
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}
